/* ==========================================================================
   Shop near you — consent-gated proximity sorting.

   Contract, in order of importance:

   1. navigator.geolocation.getCurrentPosition is called from exactly one
      place: the click handler of the locate button. Not on load, not from an
      IntersectionObserver, not on shopify:section:load, not on a retry timer.
      A prompt the visitor did not ask for is a permanent loss — denial is
      sticky per origin and there is no API to ask again.
   2. The position never leaves the browser. Distances are computed against
      pins already present in the page — nothing is fetched or beaconed. It
      is stored at full precision (the merchant asked for exact distances, and
      a rounded coordinate would make them drift after a reload), in the
      visitor's own storage, cleared by the "Clear location" control.
   3. Every failure has a way forward — the manual picker — and no failure
      offers a button that can only fail again.
   ========================================================================== */
(function () {
  'use strict';

  var R_KM = 6371.0088; // IUGG mean earth radius R1
  var R_MI = 3958.7613;
  var STORAGE_VERSION = 1;

  /* Distances are shown exactly, so the persisted coordinate has to be exact
     too — a 1-decimal round (~11 km) would make the numbers change after a
     reload. The position still never leaves the browser: it is compared
     against pins already in the page, and "Clear location" wipes it. */
  var STORE_PRECISION = 5;

  var GEO_OPTIONS = {
    // A wifi/cell fix is accurate to 1-50km. Every pin is >=40km from the
    // next, so true GPS would cost 5-30s and a lit location indicator to
    // change nothing. High accuracy here is a cost with no matching benefit.
    enableHighAccuracy: false,
    timeout: 8000,
    // Half an hour: nobody crosses a city-ranking boundary in that time, and
    // a cached fix returns without waking the radio.
    maximumAge: 1800000
  };
  var GEO_RETRY_OPTIONS = { enableHighAccuracy: false, timeout: 12000, maximumAge: Infinity };
  var WATCHDOG_MS = 12000;
  var SLOW_HINT_MS = 4000;
  var STATUS_DEBOUNCE_MS = 150;

  /* Survives the theme editor replacing a section node on every settings
     change, so a merchant does not re-consent after each slider drag. */
  var sessionCache = {};

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  /* --- Storage ---------------------------------------------------------
     Touching window.localStorage at all throws in Chrome when all cookies
     are blocked, and setItem throws in some private-mode Safari builds. A
     storage failure degrades to memory-only; it is never an error state the
     visitor has to read about. */
  function makeStorage(persistent) {
    var memory = {};
    var backing = null;
    try {
      backing = persistent ? window.localStorage : window.sessionStorage;
      var probe = '__ny__';
      backing.setItem(probe, '1');
      backing.removeItem(probe);
    } catch (e) {
      backing = null;
    }
    return {
      get: function (key) {
        try {
          return backing ? backing.getItem(key) : memory[key] || null;
        } catch (e) {
          return memory[key] || null;
        }
      },
      set: function (key, value) {
        try {
          if (backing) backing.setItem(key, value);
          else memory[key] = value;
        } catch (e) {
          memory[key] = value;
        }
      },
      remove: function (key) {
        try {
          if (backing) backing.removeItem(key);
        } catch (e) {
          /* fall through */
        }
        delete memory[key];
      }
    };
  }

  /* --- Geometry --------------------------------------------------------- */
  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  function haversine(lat1, lng1, lat2, lng2, radius) {
    var p1 = toRad(lat1);
    var p2 = toRad(lat2);
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    // The clamp is load-bearing: for near-antipodal points floating-point
    // error pushes sqrt(a) just past 1 and Math.asin returns NaN, which
    // would poison the whole sort.
    return 2 * radius * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function validCoords(lat, lng) {
    if (!isFinite(lat) || !isFinite(lng)) return false;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
    // Null island — a known placeholder from WebViews and from blank settings.
    if (lat === 0 && lng === 0) return false;
    return true;
  }

  function roundCoord(value) {
    var factor = Math.pow(10, STORE_PRECISION);
    return Math.round(value * factor) / factor;
  }

  /* --- Distance copy ----------------------------------------------------
     Exact, not bucketed. One decimal below 10 so a short hop reads as
     "0.4 mi" rather than "0 mi"; whole numbers above that, where a decimal
     is noise. This is only honest because the pins carry real centroids —
     see the coordinate note in the README. */
  function formatDistance(value, units, long) {
    var unit = units === 'mi' ? 'mile' : 'kilometer';
    var digits = value < 10 ? 1 : 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'unit',
        unit: unit,
        unitDisplay: long ? 'long' : 'short',
        maximumFractionDigits: digits,
        minimumFractionDigits: 0
      }).format(value);
    } catch (e) {
      // style:'unit' is ES2020; older WebViews throw.
      return value.toFixed(digits) + ' ' + units;
    }
  }

  function interpolate(template, values) {
    return String(template).replace(/\[\[(\w+)\]\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /* --- Permissions ------------------------------------------------------
     Fails in three different shapes: absent (old Safari), a rejected promise
     (Safari 16+ knows the API but not the 'geolocation' descriptor), or a
     usable state. Everything unusable normalizes to 'unknown', and every
     path below has to be correct with no permission information at all —
     that is the majority of iOS traffic. Querying never prompts. */
  function queryPermission() {
    return new Promise(function (resolve) {
      var status = null;
      try {
        if (!navigator.permissions || !navigator.permissions.query) return resolve({ state: 'unknown' });
        var result = navigator.permissions.query({ name: 'geolocation' });
        if (!result || typeof result.then !== 'function') return resolve({ state: 'unknown' });
        result.then(
          function (permissionStatus) {
            status = permissionStatus;
            resolve({ state: permissionStatus.state || 'unknown', status: status });
          },
          function () {
            resolve({ state: 'unknown' });
          }
        );
      } catch (e) {
        resolve({ state: 'unknown' });
      }
    });
  }

  /* ====================================================================== */
  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.__nearYouInit) return;
    sectionEl.__nearYouInit = true;

    var configTag = sectionEl.querySelector('[data-near-you-config]');
    if (!configTag) return;
    var config = safeJsonParse(configTag.textContent);
    if (!config) return;

    var strings = config.strings || {};
    var units = config.units === 'mi' ? 'mi' : 'km';
    var radius = units === 'mi' ? R_MI : R_KM;
    var maxResults = Number(config.maxResults) || 8;
    var maxDistance = Number(config.maxDistance) || 0;
    var minResults = Number(config.minResults) || 3;
    var debug = !!config.debug;
    var showDistance = config.showDistance !== false;
    var ttlDays = Number(config.ttlDays) || 0;
    var storage = makeStorage(ttlDays > 0);
    var storageKey = config.storageKey || 'eastcoast:near-you';
    var sectionId = sectionEl.dataset.sectionId || '';

    var grid = sectionEl.querySelector('[data-near-you-grid]');
    var consentPanel = sectionEl.querySelector('[data-near-you-consent]');
    var errorPanel = sectionEl.querySelector('[data-near-you-error]');
    var errorTitle = sectionEl.querySelector('[data-near-you-error-title]');
    var errorBody = sectionEl.querySelector('[data-near-you-error-body]');
    var retryBtn = sectionEl.querySelector('[data-near-you-retry]');
    var resolvedBar = sectionEl.querySelector('[data-near-you-resolved]');
    var resolvedLabel = sectionEl.querySelector('[data-near-you-resolved-label]');
    var clearBtn = sectionEl.querySelector('[data-near-you-clear]');
    var manualPanel = sectionEl.querySelector('[data-near-you-manual]');
    var manualToggles = sectionEl.querySelectorAll('[data-near-you-manual-toggle]');
    var locateBtn = sectionEl.querySelector('[data-near-you-locate]');
    var locateLabel = sectionEl.querySelector('[data-near-you-locate-label]');
    var statusEl = sectionEl.querySelector('[data-near-you-status]');
    /* Heading and grid are toggled as one node. The invariant the rest of this
       file maintains: the results are on screen if and only if an origin has
       been applied. Nothing claims to be near you before we know where you
       are. */
    var results = sectionEl.querySelector('[data-near-you-results]');
    var resultsHeading = sectionEl.querySelector('[data-near-you-results-heading]');

    if (!grid) return;

    var cards = [].slice.call(grid.querySelectorAll('[data-near-you-card]'));
    if (!cards.length) return;

    /* Every listener is registered against this signal so a single abort()
       on shopify:section:unload removes all of them. globe-hero.js leaks a
       window listener per re-render for want of this. */
    var controller = new AbortController();
    var signal = controller.signal;
    var listen = function (target, type, handler, options) {
      var opts = options || {};
      opts.signal = signal;
      target.addEventListener(type, handler, opts);
    };

    var generation = 0;
    var inFlight = false;
    var watchdog = null;
    var slowTimer = null;
    var statusTimer = null;
    var lastStatus = '';
    var lastToggle = null;
    var permissionState = 'unknown';
    var permissionStatus = null;
    var retryUsed = false;

    var pins = cards
      .map(function (card, index) {
        return {
          el: card,
          index: index,
          lat: parseFloat(card.dataset.lat),
          lng: parseFloat(card.dataset.lng),
          tier: parseInt(card.dataset.tier, 10) === 2 ? 2 : 1,
          title: card.dataset.title || ''
        };
      })
      .filter(function (pin) {
        // A blank coordinate would otherwise land at 0,0 and rank first for
        // anyone in the Gulf of Guinea.
        if (validCoords(pin.lat, pin.lng)) return true;
        pin.el.remove();
        return false;
      });

    if (!pins.length) return;

    /* --- Status ---------------------------------------------------------
       One polite sentence per transition. Repeated identical text in a live
       region is often dropped, so a repeat is cleared first. */
    function announce(message) {
      if (!statusEl || !message) return;
      clearTimeout(statusTimer);
      statusTimer = setTimeout(function () {
        if (message === lastStatus) {
          statusEl.textContent = '';
          window.requestAnimationFrame(function () {
            statusEl.textContent = message;
          });
        } else {
          statusEl.textContent = message;
        }
        lastStatus = message;
      }, STATUS_DEBOUNCE_MS);
    }

    /* --- Focus ----------------------------------------------------------
       "Still there" has to mean still RENDERED, not merely still in the
       document. Panels here are toggled with the hidden attribute, so a
       control the visitor was on is almost always still connected — a
       document.contains() test would pass every time and the focus handoff
       would never run, leaving focus on <body> and restarting Tab at the top
       of the page. getClientRects() is empty for anything inside a
       display:none subtree, and unlike offsetParent it stays correct for
       position:fixed. */
    function isRendered(el) {
      return !!(el && el.isConnected && el.getClientRects().length);
    }

    /* Only moves focus when the element that had it has actually left the
       screen — otherwise a state change would yank the visitor out of
       wherever they are. */
    function focusIfOrphaned(previousActive, target) {
      if (!previousActive || previousActive === document.body) return;
      if (isRendered(previousActive)) return;
      if (!isRendered(target)) return;
      target.focus({ preventScroll: true });
    }

    function setPanel(el, visible) {
      if (el) el.hidden = !visible;
    }

    /* --- Manual picker --------------------------------------------------- */
    function visibleToggle() {
      for (var i = 0; i < manualToggles.length; i++) {
        if (isRendered(manualToggles[i])) return manualToggles[i];
      }
      return null;
    }

    function setManualOpen(open, originButton) {
      if (!manualPanel) return;
      var hadFocusInside = manualPanel.contains(document.activeElement);
      manualPanel.hidden = !open;
      [].forEach.call(manualToggles, function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      if (open) {
        if (originButton) lastToggle = originButton;
        var first = manualPanel.querySelector('[data-near-you-pick]');
        if (first) first.focus({ preventScroll: true });
        return;
      }

      // Closing. Hand focus back to a toggle that is actually on screen: the
      // panel owning the original toggle may have been swapped out since, and
      // the picker is often opened from an error path with no origin button
      // at all — focusing a display:none node is a silent no-op that strands
      // the visitor on <body>.
      if (!hadFocusInside) return;
      var back =
        (isRendered(lastToggle) ? lastToggle : null) ||
        visibleToggle() ||
        (isRendered(locateBtn) ? locateBtn : null) ||
        (isRendered(resultsHeading) ? resultsHeading : null);
      if (back) back.focus({ preventScroll: true });
    }

    [].forEach.call(manualToggles, function (btn) {
      listen(btn, 'click', function () {
        setManualOpen(manualPanel.hidden, btn);
      });
    });

    if (manualPanel) {
      listen(manualPanel, 'keydown', function (event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setManualOpen(false);
        }
      });
      listen(manualPanel, 'click', function (event) {
        var pick = event.target.closest('[data-near-you-pick]');
        if (!pick) return;
        var lat = parseFloat(pick.dataset.lat);
        var lng = parseFloat(pick.dataset.lng);
        if (!validCoords(lat, lng)) return;
        generation += 1;
        setManualOpen(false);
        apply({ lat: lat, lng: lng, mode: 'manual', title: pick.dataset.title || '' }, true);
      });
    }

    /* --- Rendering ------------------------------------------------------- */
    function decorate(pin, origin) {
      var distanceEl = pin.el.querySelector('[data-near-you-distance]');
      var spokenEl = pin.el.querySelector('[data-near-you-distance-spoken]');
      if (!distanceEl || !spokenEl) return;

      if (!origin || !showDistance || pin.tier === 2) {
        // A centroid is not a place anyone is a measurable distance from.
        distanceEl.hidden = !(origin && pin.tier === 2);
        distanceEl.textContent = origin && pin.tier === 2 ? strings.regionCollection || '' : '';
        spokenEl.textContent = origin && pin.tier === 2 ? strings.regionCollection || '' : '';
        return;
      }

      distanceEl.hidden = false;
      distanceEl.textContent = interpolate(strings.distanceAway || '[[distance]]', {
        distance: formatDistance(pin.distance, units, false)
      });
      spokenEl.textContent = interpolate(strings.distanceAway || '[[distance]]', {
        distance: formatDistance(pin.distance, units, true)
      });
    }

    function render(origin) {
      var ordered = pins.slice();

      if (origin) {
        ordered.forEach(function (pin) {
          pin.distance = haversine(origin.lat, origin.lng, pin.lat, pin.lng, radius);
        });
        // Tie-break on the merchant's original order rather than leaving it
        // to the engine.
        ordered.sort(function (a, b) {
          return a.distance - b.distance || a.index - b.index;
        });
      }

      // Move the nodes. CSS `order` would desynchronise reading and tab order
      // from what is on screen (WCAG 1.3.2 / 2.4.3).
      var fragment = document.createDocumentFragment();
      var shown = 0;
      ordered.forEach(function (pin, position) {
        // Once a location is known, only genuinely nearby places are worth
        // showing — but never an empty grid, so the first `minResults` are
        // kept regardless of how far away they are.
        var visible;
        if (!origin) {
          visible = position < maxResults;
        } else if (position < minResults) {
          visible = true;
        } else if (maxDistance > 0 && pin.distance > maxDistance) {
          visible = false;
        } else {
          visible = shown < maxResults;
        }
        if (visible) shown++;
        pin.el.classList.toggle('is-overflow', !visible);
        pin.el.classList.toggle('is-nearest', !!origin && position === 0 && pin.tier === 1);
        var badge = pin.el.querySelector('[data-near-you-badge]');
        if (badge) badge.hidden = !(origin && position === 0 && pin.tier === 1);
        decorate(pin, origin);
        fragment.appendChild(pin.el);
      });
      grid.setAttribute('aria-busy', 'false');
      grid.appendChild(fragment);

      return ordered;
    }

    function apply(origin, persist) {
      var previousActive = document.activeElement;
      stopTimers();

      var ordered = render(origin);
      var nearest = ordered[0];

      clearRequesting();
      setPanel(consentPanel, false);
      setPanel(errorPanel, false);
      setPanel(resolvedBar, true);
      setPanel(results, true);

      if (resolvedLabel) {
        resolvedLabel.textContent =
          origin.mode === 'manual'
            ? interpolate(strings.usingManual || '', { title: origin.title || '' })
            : strings.usingGps || '';
      }

      if (persist) {
        sessionCache[sectionId] = origin;
        storage.set(
          storageKey,
          JSON.stringify({
            v: STORAGE_VERSION,
            mode: origin.mode,
            // Rounded before it is written: enough to rank cities, not
            // enough to place a household.
            lat: roundCoord(origin.lat),
            lng: roundCoord(origin.lng),
            title: origin.title || '',
            ts: Date.now()
          })
        );
      }

      if (debug) {
        console.info('[near-you] origin', {
          mode: origin.mode,
          title: origin.title || null,
          latitude: origin.lat,
          longitude: origin.lng,
          units: units,
          maxDistance: maxDistance || '(no limit)',
          minResults: minResults,
          maxResults: maxResults
        });
        var table = ordered.map(function (pin, i) {
          return {
            rank: i + 1,
            place: pin.title,
            scope: pin.tier === 2 ? 'region' : 'city',
            distance: pin.distance == null ? null : Number(pin.distance.toFixed(3)),
            units: units,
            lat: pin.lat,
            lng: pin.lng,
            shown: !pin.el.classList.contains('is-overflow')
          };
        });
        if (console.table) console.table(table);
        else console.info('[near-you] ranking', table);
      }

      if (nearest) {
        var showsNumber = showDistance && nearest.tier === 1 && isFinite(nearest.distance);
        announce(
          showsNumber
            ? interpolate(strings.statusSorted || '', {
                title: nearest.title,
                distance: interpolate(strings.distanceAway || '', {
                  distance: formatDistance(nearest.distance, units, true)
                })
              })
            : interpolate(strings.statusSortedPlain || '', { title: nearest.title })
        );
      }

      focusIfOrphaned(previousActive, resultsHeading);
    }

    function reset(announceIt) {
      var previousActive = document.activeElement;
      generation += 1;
      stopTimers();
      delete sessionCache[sectionId];
      storage.remove(storageKey);
      render(null);
      setPanel(resolvedBar, false);
      setPanel(errorPanel, false);
      setPanel(results, false);
      setPanel(consentPanel, true);
      clearRequesting();
      if (announceIt) announce(strings.statusCleared || '');
      focusIfOrphaned(previousActive, locateBtn);
    }

    function showError(titleKey, bodyKey, allowRetry) {
      var previousActive = document.activeElement;
      stopTimers();
      clearRequesting();
      grid.setAttribute('aria-busy', 'false');
      setPanel(consentPanel, false);
      setPanel(resolvedBar, false);
      // Holds the invariant: no origin, no results.
      setPanel(results, false);
      setPanel(errorPanel, true);
      if (errorTitle) errorTitle.textContent = strings[titleKey] || '';
      if (errorBody) errorBody.textContent = strings[bodyKey] || '';
      // A retry button after a denial could only ever fail again.
      if (retryBtn) retryBtn.hidden = !allowRetry;
      focusIfOrphaned(previousActive, errorTitle);
    }

    function stopTimers() {
      clearTimeout(watchdog);
      clearTimeout(slowTimer);
      watchdog = null;
      slowTimer = null;
    }

    /* Owns all three panels, so "requesting" looks the same whether it was
       entered from the consent card or from Retry inside the error panel.
       Without this the retry button left the stale error text on screen and
       wrote "Finding you…" into a display:none node — ~20s of a UI that
       looks frozen, and a live retry button inviting more clicks. */
    function setRequesting() {
      stopTimers();
      setPanel(errorPanel, false);
      setPanel(resolvedBar, false);
      setPanel(results, false);
      setPanel(consentPanel, true);
      if (retryBtn) retryBtn.hidden = true;
      if (locateBtn) {
        locateBtn.hidden = false;
        // aria-disabled, not the disabled property: a disabled button is
        // removed from the focus order, which would blur the very control
        // the visitor just activated. The inFlight guard does the real work.
        locateBtn.setAttribute('aria-disabled', 'true');
        if (locateLabel) locateLabel.textContent = strings.locating || '';
      }
      grid.setAttribute('aria-busy', 'true');
      announce(strings.statusRequesting || '');
    }

    function clearRequesting() {
      inFlight = false;
      if (locateBtn) {
        locateBtn.removeAttribute('aria-disabled');
        if (locateLabel) locateLabel.textContent = strings.consentButton || '';
      }
    }

    /* --- The one call site ----------------------------------------------- */
    function requestPosition(options, isRetry) {
      var myGeneration = generation;

      // The spec's own `timeout` starts counting only after permission is
      // granted, and several in-app WebViews never invoke either callback.
      // This is the only guarantee the UI leaves the requesting state.
      watchdog = setTimeout(function () {
        if (myGeneration !== generation) return;
        generation += 1;
        showError('unavailableTitle', 'unavailableBody', true);
        setManualOpen(true);
      }, WATCHDOG_MS);

      slowTimer = setTimeout(function () {
        if (myGeneration !== generation) return;
        if (locateLabel) locateLabel.textContent = strings.locatingSlow || '';
      }, SLOW_HINT_MS);

      navigator.geolocation.getCurrentPosition(
        function (position) {
          if (myGeneration !== generation) return;
          stopTimers();
          var coords = position && position.coords;
          if (debug && coords) {
            // Temporary diagnostic (section setting "Log location to console").
            console.info('[near-you] raw fix', {
              latitude: coords.latitude,
              longitude: coords.longitude,
              accuracyMetres: coords.accuracy,
              altitude: coords.altitude,
              heading: coords.heading,
              speed: coords.speed,
              timestamp: position.timestamp,
              age: position.timestamp ? Date.now() - position.timestamp + 'ms' : null,
              retry: !!isRetry
            });
          }
          if (!coords || !validCoords(coords.latitude, coords.longitude)) {
            showError('unavailableTitle', 'unavailableBody', true);
            setManualOpen(true);
            return;
          }
          apply({ lat: coords.latitude, lng: coords.longitude, mode: 'gps' }, true);
        },
        function (error) {
          if (myGeneration !== generation) return;
          stopTimers();
          var code = error && error.code;
          if (code === 1) {
            // Sticky per origin, and indistinguishable from an OS-level
            // toggle or a frame without allow="geolocation". No retry.
            permissionState = 'denied';
            if (locateBtn) locateBtn.hidden = true;
            showError(
              isDesignMode() ? 'editorTitle' : 'deniedTitle',
              isDesignMode() ? 'editorBody' : 'deniedBody',
              false
            );
            setManualOpen(true);
            return;
          }
          if (code === 3 && !retryUsed) {
            // Any cached fix is a reasonable last resort at city granularity.
            retryUsed = true;
            requestPosition(GEO_RETRY_OPTIONS, true);
            return;
          }
          if (code === 3) {
            showError('timeoutTitle', 'timeoutBody', true);
          } else {
            showError('unavailableTitle', 'unavailableBody', true);
          }
          setManualOpen(true);
        },
        options
      );
    }

    function locate() {
      if (isDesignMode()) {
        // The editor frames the storefront cross-origin without
        // allow="geolocation", so this can only ever return code 1.
        showError('editorTitle', 'editorBody', false);
        setManualOpen(true);
        return;
      }
      if (!window.isSecureContext || !navigator.geolocation) {
        // Chrome deletes the property on insecure origins while Firefox
        // keeps it and always errors, so the secure-context check has to
        // come first or the two get conflated.
        showError('unsupportedTitle', 'unsupportedBody', false);
        setManualOpen(true);
        return;
      }
      // Single-flight: a second press must not start a second request, nor
      // orphan the first one's watchdog pair.
      if (inFlight) return;
      inFlight = true;
      generation += 1;
      retryUsed = false;
      setRequesting();
      requestPosition(GEO_OPTIONS, false);
    }

    if (locateBtn) listen(locateBtn, 'click', locate);
    if (retryBtn) listen(retryBtn, 'click', locate);
    if (clearBtn) {
      listen(clearBtn, 'click', function () {
        reset(true);
      });
    }

    /* --- Rehydrate — never a prompt -------------------------------------- */
    function storedOrigin() {
      if (sessionCache[sectionId]) return sessionCache[sectionId];
      var raw = storage.get(storageKey);
      if (!raw) return null;
      var parsed = safeJsonParse(raw);
      if (!parsed || parsed.v !== STORAGE_VERSION) return null;
      if (!validCoords(parsed.lat, parsed.lng)) return null;
      if (ttlDays > 0) {
        var age = Date.now() - (parsed.ts || 0);
        if (!(age >= 0) || age > ttlDays * 86400000) {
          storage.remove(storageKey);
          return null;
        }
      }
      return { lat: parsed.lat, lng: parsed.lng, mode: parsed.mode || 'gps', title: parsed.title || '' };
    }

    var restored = storedOrigin();
    if (restored) {
      // Re-applying a stored decision is not a new request for consent.
      apply(restored, false);
    }

    queryPermission().then(function (result) {
      if (signal.aborted) return;
      permissionState = result.state;
      permissionStatus = result.status || null;

      if (permissionState === 'denied' && !restored) {
        // Never invite a press that can only fail. But this is a state the
        // visitor arrived with, not one they just caused, so it must not
        // raise the role="alert" panel — that would fire an assertive
        // announcement on page load. The consent card keeps its framing and
        // simply loses the button it cannot honour, leaving the manual
        // picker as the visible path.
        if (locateBtn) locateBtn.hidden = true;
        var consentText = consentPanel && consentPanel.querySelector('.near-you__panel-text');
        if (consentText) {
          consentText.textContent =
            (isDesignMode() ? strings.editorBody : strings.deniedBody) || consentText.textContent;
        }
      }

      if (permissionStatus) {
        var onChange = function () {
          if (permissionStatus.state === 'denied') {
            if (locateBtn) locateBtn.hidden = true;
          } else if (locateBtn) {
            locateBtn.hidden = false;
          }
        };
        if (typeof permissionStatus.addEventListener === 'function') {
          listen(permissionStatus, 'change', onChange);
        } else {
          permissionStatus.onchange = onChange;
        }
      }
    });

    sectionEl.__nearYouTeardown = function () {
      stopTimers();
      clearTimeout(statusTimer);
      generation += 1;
      controller.abort();
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-near-you]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target) return;
    var sections = target.matches && target.matches('[data-near-you]') ? [target] : target.querySelectorAll('[data-near-you]');
    [].forEach.call(sections, function (el) {
      if (typeof el.__nearYouTeardown === 'function') el.__nearYouTeardown();
    });
  });

  // Referenced so the reduced-motion helper is not dropped by minifiers and
  // stays available to the stylesheet contract documented in the CSS.
  if (prefersReducedMotion()) {
    document.documentElement.classList.add('near-you-reduced-motion');
  }
})();
